import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { friendlySlackTokenError } from "@/lib/slack-errors";
import {
  ConnectionSection,
  DetailList,
  DetailRow,
  DisconnectButton,
  Notice,
} from "@/pages/container-tabs/connections/ConnectionParts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useState } from "react";

// The workspace's Slack page: the Boo app (installed once, answering through
// one Boo) and the workspace token Boos use to make Slack apps of their own.
export function WorkspaceSlack({ orgId }: { orgId: string }) {
  return (
    <>
      <BooAppSection orgId={orgId} />
      <OwnAppsSection />
    </>
  );
}

function BooAppSection({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const status = useQuery({ queryKey: ["slack-boo-status"], queryFn: () => api.slack.boo.status() });
  const disconnect = useMutation({
    mutationFn: () => api.slack.boo.disconnect(),
    onSuccess: () => {
      setDisconnectOpen(false);
      queryClient.invalidateQueries({ queryKey: ["slack-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["containers"] });
    },
  });
  const d = status.data;

  return (
    <ConnectionSection title="Boo app" description="Boo appears in Slack as Boo, answering through one Boo at a time.">
      {status.isLoading ? (
        <LoadingIndicator message="Checking Slack…" />
      ) : status.isError || !d ? (
        <InlineError error={status.error ?? "Failed to check Slack status"} />
      ) : d.installed || d.workspaceConnected ? (
        <DetailList>
          <DetailRow
            term="Installed in"
            action={
              d._permissions?.canDisconnect !== false && (
                <DisconnectButton pending={disconnect.isPending} onClick={() => setDisconnectOpen(true)} />
              )
            }
          >
            {d.teamName ?? "Your Slack workspace"}
          </DetailRow>
          <DetailRow
            term="Answers through"
            action={
              d.containerId && (
                <Button asChild size="sm" variant="outline">
                  <Link
                    to="/orgs/$orgId/containers/$containerId/connections/$connection"
                    params={{ orgId, containerId: d.containerId, connection: "slack" }}
                  >
                    Open
                  </Link>
                </Button>
              )
            }
          >
            {d.containerName ?? (d.containerId ? "A Boo" : "No Boo yet. Pick one from its Connections.")}
          </DetailRow>
        </DetailList>
      ) : (
        <Notice>
          <p>Not installed. Add Boo to Slack from a Boo&apos;s Connections, where you pick the Boo that answers.</p>
        </Notice>
      )}
      {disconnect.isError && <InlineError error={disconnect.error} />}
      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect Boo from Slack?"
        description="This removes the Boo app from your workspace. Slack messages stop reaching your Boo."
        confirmLabel="Disconnect"
        loading={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
      />
    </ConnectionSection>
  );
}

function OwnAppsSection() {
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const status = useQuery({ queryKey: ["slack-workspace"], queryFn: () => api.slack.custom.workspace.status() });
  const clear = useMutation({
    mutationFn: () => api.slack.custom.workspace.clear(),
    onSuccess: () => {
      setUnlinkOpen(false);
      queryClient.invalidateQueries({ queryKey: ["slack-workspace"] });
    },
  });
  const d = status.data;

  return (
    <ConnectionSection
      title="Boos' own apps"
      description="Each Boo can get a Slack app of its own, with its own name and icon. They're made with your workspace's token."
    >
      {status.isLoading ? (
        <LoadingIndicator message="Checking workspace…" />
      ) : status.isError || !d ? (
        <InlineError error={status.error ?? "Failed to check workspace status"} />
      ) : d.connected ? (
        <DetailList>
          <DetailRow
            term="Linked to"
            action={
              d._permissions?.canDisconnect !== false && (
                <Button size="sm" variant="outline" disabled={clear.isPending} onClick={() => setUnlinkOpen(true)}>
                  Unlink
                </Button>
              )
            }
          >
            {d.teamName ?? "Your Slack workspace"}
          </DetailRow>
        </DetailList>
      ) : (
        <Notice
          action={
            d._permissions?.canInstall !== false && (
              <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)}>
                Link workspace
              </Button>
            )
          }
        >
          <p>Not linked. Link your Slack workspace so Boos can make apps of their own.</p>
        </Notice>
      )}
      {clear.isError && <InlineError error={clear.error} />}
      <ConnectSlackDialog open={linkOpen} onClose={() => setLinkOpen(false)} />
      <ConfirmDialog
        open={unlinkOpen}
        onOpenChange={setUnlinkOpen}
        title="Unlink Slack workspace?"
        description="Boos' existing apps stay installed, but no new ones can be made until you link it again."
        confirmLabel="Unlink"
        loading={clear.isPending}
        onConfirm={() => clear.mutate()}
      />
    </ConnectionSection>
  );
}

export function ConnectSlackDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [refreshToken, setRefreshToken] = useState("");

  const save = useMutation({
    mutationFn: (token: string) => api.slack.custom.workspace.save(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["slack-workspace"] });
      close();
    },
  });
  // Every way out clears the token and any error, so reopening starts fresh.
  const close = () => {
    setRefreshToken("");
    save.reset();
    onClose();
  };

  const trimmed = refreshToken.trim();
  const tokenValid = trimmed.startsWith("xoxe-") && trimmed.length > 20;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link your Slack workspace</DialogTitle>
          <DialogDescription>
            One-time setup. After this, every Boo in your org can spin up its own Slack bot.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 pt-2">
          <ol className="text-sm list-decimal list-inside space-y-1.5 pl-1">
            <li>
              Open{" "}
              <a
                href="https://api.slack.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                api.slack.com/apps
              </a>{" "}
              and sign in to Slack.
            </li>
            <li>
              Top-right profile menu → <strong>App configuration tokens</strong>.
            </li>
            <li>
              Pick your workspace → <strong>Generate Token</strong>.
            </li>
            <li>
              Paste the refresh token (starts with <code>xoxe-1-…</code>) below.
            </li>
          </ol>
          <Alert className="px-2 py-1.5 bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-500/10 dark:border-sky-500/20 dark:text-sky-200 *:[svg]:size-3.5 *:[svg]:translate-y-0 [&>[data-slot=alert-description]]:text-xs [&>[data-slot=alert-description]]:text-sky-900/80 dark:[&>[data-slot=alert-description]]:text-sky-200/80">
            <Info />
            <AlertDescription>
              If your workspace has app approval turned on, an admin will need
              to approve the new bot before it can post.
            </AlertDescription>
          </Alert>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="slack-refresh-token" className="text-xs">
              Refresh token
            </Label>
            <Input
              id="slack-refresh-token"
              type="text"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              value={refreshToken}
              onChange={(e) => setRefreshToken(e.target.value)}
              placeholder="xoxe-1-..."
            />
          </div>
          {save.isError && (
            <InlineError error={friendlySlackTokenError(save.error)} />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={!tokenValid}
              loading={save.isPending}
              onClick={() => save.mutate(trimmed)}
            >
              Link workspace
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
